import { useMemo, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EDITOR_FIELD_CATALOG,
  EDITOR_FIELD_NAME_OF_DECEASED,
  editorTextBoxFieldGroupLabel,
  editorTextBoxFieldMenuLayoutLabel,
  editorTextBoxFieldMenuRowLabel,
  fieldMenuGroups,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCaseData,
  EditorTextBoxMount,
  FieldMenuLayout,
  FieldMenuRow,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import Icon from 'components/priority/Icon/Icon'
import * as S from './EditorTextBoxToolbar.styled'

type EditorTextBoxFieldMenuProps = {
  caseData?: EditorCaseData
  /** The headings whose rows show. */
  openGroups: ReadonlySet<string>
  onToggleGroup: (group: string) => void
  /** Runs an insert through the box's mount, at the caret or over the selection. */
  onInsert: (insert: (mount: EditorTextBoxMount) => void) => void
  /** The mouse-down handler of a row's details button, which opens the row's modal. */
  onConfigure: (
    fieldKey: string,
    serviceId?: string,
    rowType?: string,
  ) => (e: ReactMouseEvent<HTMLElement>) => void
}

/**
 * The Insert detail menu's list: each heading, and under it the rows and
 * layouts it holds while it is open; a row with no heading stands in the
 * list itself. A row inserts on click, and the details button ending it
 * opens its modal. A layout row inserts its paragraphs on click and has no
 * details button: no modal covers several details at once.
 */
export const EditorTextBoxFieldMenu = ({
  caseData,
  openGroups,
  onToggleGroup,
  onInsert,
  onConfigure,
}: EditorTextBoxFieldMenuProps) => {
  const { t } = useTranslation()
  const groups = useMemo(
    () => fieldMenuGroups(EDITOR_FIELD_CATALOG, caseData),
    [caseData],
  )

  const renderRow = (row: FieldMenuRow) => {
    const { entry, service, rowType } = row
    const insert = () =>
      onInsert((mount) => {
        if (service) mount.insertField(entry.key, { serviceId: service.id })
        else if (rowType !== undefined)
          mount.insertCaseDetailTable(entry.key, { rowType })
        else if (entry.kind === 'tabular')
          mount.insertCaseDetailTable(entry.key)
        else mount.insertField(entry.key)
      })
    const details = service
      ? {
          title: t('editor.textBox.toolbar.configureDetails'),
          attrs: { 'data-editor-text-field-configure-service': service.id },
        }
      : rowType !== undefined
        ? {
            title: t('editor.textBox.toolbar.configureRows'),
            attrs: { 'data-editor-text-field-configure-row-type': rowType },
          }
        : {
            title:
              entry.key === EDITOR_FIELD_NAME_OF_DECEASED
                ? t('editor.textBox.toolbar.configureNameOrder')
                : entry.kind === 'scalar'
                  ? t('editor.textBox.toolbar.configureValue')
                  : entry.kind === 'tabular'
                    ? t('editor.textBox.toolbar.configureRows')
                    : t('editor.textBox.toolbar.configureDetails'),
            attrs: { 'data-editor-text-field-configure': entry.key },
          }
    return (
      <S.FieldMenuSplit key={`${entry.key}:${service?.id ?? rowType ?? ''}`}>
        <S.MenuItem
          type="button"
          data-field-key={entry.key}
          {...(service ? { 'data-field-service-id': service.id } : {})}
          {...(rowType !== undefined ? { 'data-field-row-type': rowType } : {})}
          style={{ flex: 1 }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={insert}
        >
          {editorTextBoxFieldMenuRowLabel(t, row)}
        </S.MenuItem>
        <S.FieldMenuConfigure
          type="button"
          {...details.attrs}
          title={details.title}
          onMouseDown={onConfigure(entry.key, service?.id, rowType)}
        >
          <Icon icon="chevronRight" width="10px" height="10px" />
        </S.FieldMenuConfigure>
      </S.FieldMenuSplit>
    )
  }

  const renderLayout = (layout: FieldMenuLayout) => (
    <S.MenuItem
      key={layout.id}
      type="button"
      data-field-layout={layout.id}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() =>
        onInsert((mount) => mount.insertFieldParagraphs(layout.paragraphs))
      }
    >
      {editorTextBoxFieldMenuLayoutLabel(t, layout)}
    </S.MenuItem>
  )

  return (
    <S.MenuList
      data-editor-text-field-list
      $width={S.MENU_LIST_WIDTH}
      $canvasBound
    >
      {groups.flatMap(({ group, rows, layouts }) => {
        if (group === null)
          return [...rows.map(renderRow), ...layouts.map(renderLayout)]
        const expanded = openGroups.has(group)
        return [
          <div key={group}>
            <S.MenuGroupRow
              type="button"
              aria-expanded={expanded}
              data-editor-text-field-group={group}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onToggleGroup(group)}
            >
              {editorTextBoxFieldGroupLabel(t, group)}
              <S.MenuGroupChevron>
                <S.Caret $up={expanded}>
                  <Icon icon="chevronDown" width="11px" height="6px" />
                </S.Caret>
              </S.MenuGroupChevron>
            </S.MenuGroupRow>
            <S.FieldMenuRows
              {...(expanded
                ? { 'data-editor-text-field-group-expanded': group }
                : {})}
              aria-hidden={!expanded}
              hidden={!expanded}
            >
              {rows.map(renderRow)}
              {layouts.map(renderLayout)}
            </S.FieldMenuRows>
          </div>,
        ]
      })}
    </S.MenuList>
  )
}
